export const getTimeElapsed = (timestamp: number | Date | string) => {
  let timestampMs: number;

  if (timestamp instanceof Date) {
    timestampMs = timestamp.getTime();
  } else if (typeof timestamp === "number") {
    timestampMs = timestamp;
  } else {
    timestampMs = new Date(timestamp).getTime();
  }

  if (!Number.isFinite(timestampMs)) {
    return "now";
  }

  const now = Date.now();
  const diffInSeconds = Math.floor((now - timestampMs) / 1000);

  // Define time intervals
  const intervals = [
    { label: "d", seconds: 86400 }, // 1 day = 24 hours * 60 minutes * 60 seconds
    { label: "h", seconds: 3600 }, // 1 hour = 60 minutes * 60 seconds
    { label: "m", seconds: 60 }, // 1 minute = 60 seconds
    { label: "s", seconds: 1 }, // 1 second
  ];

  // Find the appropriate time interval
  for (let i = 0; i < intervals.length; i++) {
    const interval = intervals[i];
    const intervalCount = Math.floor(diffInSeconds / interval.seconds);
    if (intervalCount > 0) {
      return `${intervalCount}${interval.label}`;
    }
  }

  return "now";
};

type LegalType = "natural" | "legal";

export function createPasswordForFiles(
  legalType: LegalType,
  rfc: string,
  birthDate: string | null,
): string {
  if (legalType === "natural" && birthDate) {
    const dateParts = birthDate.split("-"); // Split the date string into parts
    const year = parseInt(dateParts[0]);
    const month = parseInt(dateParts[1]) - 1; // Month is zero-based in Date constructor
    const day = parseInt(dateParts[2]);

    const parsedBirthDate = new Date(year, month, day); // Create a Date object from the parts
    rfc = rfc.slice(0, 10); // Truncate RFC to 10 characters if longer
    const formattedBirthDate = parsedBirthDate
      .toISOString()
      .slice(0, 10)
      .replace(/-/g, ""); // Format birthdate as YYYYMMDD
    return `#${rfc}#${formattedBirthDate}#`;
  } else {
    return `#${rfc}#`;
  }
}

export const toTitleCase = (str: string) => {
  if (str == null) return "";
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const formatCurrency = (
  value: number | string | null | undefined,
) => {
  let numericValue: number | null = null;

  if (typeof value === "number") {
    numericValue = Number.isFinite(value) ? value : null;
  } else if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed !== "") {
      const directNumber = Number(trimmed);
      if (!Number.isNaN(directNumber)) {
        numericValue = directNumber;
      } else {
        const digitsOnly = trimmed.replace(/[^\d.-]/g, "");
        const parsed = Number(digitsOnly);
        numericValue = Number.isNaN(parsed) ? null : parsed;
      }
    }
  }

  const safeValue = numericValue ?? 0;

  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 0,
  }).format(safeValue);
};

export const removeAccents = (str: string): string => {
  if (!str) return str;
  // Preserve ñ/Ñ by temporarily replacing before NFD normalization
  const preserved = str.replace(/ñ/g, "\u0000").replace(/Ñ/g, "\u0001");
  const withoutAccents = preserved
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  return withoutAccents.replace(/\u0000/g, "ñ").replace(/\u0001/g, "Ñ");
};

// Re-export all input filters from inputFilters.ts for backwards compatibility
export {
  filterNameInput,
  filterStreetInput,
  filterAddressNumber,
  filterPropertyAlias,
  filterCompanyRFC,
  filterLegalEntityName,
  filterNumericInput,
  filterCityInput,
  formatPhoneDisplay,
  isEmailFormatValid,
  notifyAccentRemoved,
  handlePasteWithoutAccents,
  handlePasteWithAccentOption,
} from "./inputFilters";
